<template>
  <div class="page-container">
    <el-card>
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-input
          v-model="query.keyword"
          placeholder="姓名 或 手机号 或 身份证号"
          clearable
          style="width: 240px"
          @keyup.enter.native="handleSearch"
        />
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 表格 -->
      <el-table ref="table" :data="tableData" v-loading="loading" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="name" label="姓名" min-width="100" />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="id_card" label="身份证号" min-width="180" show-overflow-tooltip />
        <el-table-column label="性别" width="70" align="center">
          <template slot-scope="{ row }">{{ genderLabel(row.gender) }}</template>
        </el-table-column>
        <el-table-column label="年龄" width="70" align="center">
          <template slot-scope="{ row }">{{ row.age != null ? row.age + ' 岁' : '—' }}</template>
        </el-table-column>
        <el-table-column label="备注" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.remark || '—' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['order:orderContact:serviceOrders']"
              type="primary" size="mini" icon="el-icon-tickets"
              @click="openServiceOrderDialog(row)"
            >服务订单</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; text-align: right"
        @size-change="val => { query.pageSize = val; query.page = 1; fetchData() }"
        @current-change="val => { query.page = val; fetchData() }"
      />
    </el-card>

    <!-- 服务订单弹框 -->
    <el-dialog
      :title="`${currentContact.name} 的服务订单`"
      :visible.sync="svcDialog.visible"
      width="1000px"
      @close="resetSvcDialog"
    >
      <el-table
        :data="svcDialog.list"
        v-loading="svcDialog.loading"
        border
        stripe
        style="width: 100%"
      >
      <el-table-column prop="service_no" label="服务订单号" min-width="180" show-overflow-tooltip />
        <el-table-column prop="product_order_no" label="产品订单号" min-width="180" show-overflow-tooltip />
        <el-table-column prop="product_name" label="产品名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="服务状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="svcStatusType(row.status)" size="small">
              {{ row.status_label || svcStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="用户姓名" width="110">
          <template slot-scope="{ row }">{{ row.user_name || '—' }}</template>
        </el-table-column>
        <el-table-column label="用户手机号" width="130">
          <template slot-scope="{ row }">{{ row.user_phone || '—' }}</template>
        </el-table-column>
        <el-table-column label="预约上门时间" width="160">
          <template slot-scope="{ row }">{{ row.appointment_at || '—' }}</template>
        </el-table-column>
        <el-table-column label="预约上门地址" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.appointment_address || '—' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="primary" size="mini" icon="el-icon-view"
              @click="goServiceDetail(row)"
            >详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 弹框内分页 -->
      <el-pagination
        :current-page="svcDialog.page"
        :page-sizes="[10, 15, 20]"
        :page-size="svcDialog.pageSize"
        :total="svcDialog.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 14px; text-align: right"
        @size-change="val => { svcDialog.pageSize = val; svcDialog.page = 1; fetchSvcOrders() }"
        @current-change="val => { svcDialog.page = val; fetchSvcOrders() }"
      />
    </el-dialog>
  </div>
</template>

<script>
import { getOrderContactList, getOrderContactServiceOrders } from '@/api/orderContact'

const SERVICE_STATUS = {
  0: { label: '待录入', type: 'info' },
  1: { label: '待服务', type: 'warning' },
  2: { label: '待分配', type: 'warning' },
  3: { label: '待联系', type: '' },
  4: { label: '待上门', type: '' },
  5: { label: '待发件', type: '' },
  6: { label: '待检测', type: '' },
  7: { label: '已完成', type: 'success' },
  8: { label: '已退款', type: 'danger' }
}

// 模块级缓存：独立于组件生命周期，在 SPA 会话期间持久存在。
// 当用户切换到其他顶层 Layout 路由（如详情页）再返回时，
// 父 Layout 会被销毁导致 keep-alive 缓存失效，
// 使用此变量恢复上次数据，避免重新请求接口。
let _pageCache = null

export default {
  name: 'OrderContactManage',
  data() {
    const c = _pageCache
    return {
      loading: false,
      tableData: c ? c.tableData : [],
      total:     c ? c.total     : 0,
      query:     c ? { ...c.query } : { page: 1, pageSize: 15, keyword: '' },
      currentContact: {},
      svcDialog: {
        visible: false,
        loading: false,
        list: [],
        total: 0,
        page: 1,
        pageSize: 15
      }
    }
  },
  created() {
    if (!_pageCache) {
      this.fetchData()
    }
  },
  activated() {
    if (this._restoreDialog) {
      this._restoreDialog = false
      this.$nextTick(() => { this.svcDialog.visible = true })
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = { page: this.query.page, pageSize: this.query.pageSize }
        if (this.query.keyword) params.keyword = this.query.keyword
        const res = await getOrderContactList(params)
        const d = res.data || {}
        this.tableData = d.list || []
        this.total = d.total || 0
        _pageCache = { tableData: this.tableData, total: this.total, query: { ...this.query } }
        this.$nextTick(() => this.$refs.table && this.$refs.table.doLayout())
      } finally {
        this.loading = false
      }
    },
    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() {
      this.query = { page: 1, pageSize: 15, keyword: '' }
      this.fetchData()
    },

    openServiceOrderDialog(row) {
      this.currentContact = row
      this.svcDialog.page = 1
      this.svcDialog.pageSize = 15
      this.svcDialog.visible = true
      this.fetchSvcOrders()
    },
    resetSvcDialog() {
      // 通过 goServiceDetail 跳转导致的关闭，保留数据供返回后复原，不做清空
      if (this._restoreDialog) return
      this.svcDialog.list = []
      this.svcDialog.total = 0
      this.svcDialog.page = 1
    },
    async fetchSvcOrders() {
      this.svcDialog.loading = true
      try {
        const res = await getOrderContactServiceOrders({
          id: this.currentContact.id,
          page: this.svcDialog.page,
          pageSize: this.svcDialog.pageSize
        })
        const d = res.data || {}
        this.svcDialog.list = d.list || []
        this.svcDialog.total = d.total || 0
      } finally {
        this.svcDialog.loading = false
      }
    },
    goServiceDetail(row) {
      // 关闭弹框避免遮罩层出现在其他页面，返回后由 activated 重新打开
      this._restoreDialog = true
      this.svcDialog.visible = false
      this.$router.push(`/order/service/detail?id=${row.id}`)
    },

    genderLabel(g) {
      if (g === 1) return '男'
      if (g === 2) return '女'
      return '—'
    },
    svcStatusType(s) { return (SERVICE_STATUS[s] || {}).type || '' },
    svcStatusLabel(s) { return (SERVICE_STATUS[s] || {}).label || String(s) }
  }
}
</script>

<style lang="scss" scoped>
.search-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
</style>
